import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs/services/workspace-dialog';
import {
  navigationActionId,
  NavigationActivationError,
} from '@affine/core/modules/streamlain/copilot/workspace-navigation-catalog';
import { WorkspaceViewContextService } from '@affine/core/modules/streamlain/copilot/workspace-view-context';
import type {
  NavigationArgs,
  WorkspaceNavigationTarget,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { Service } from '@toeverything/infra';

import type { SettingsMemory } from './settings-memory';
import type { SettingState } from './types';

export const SETTINGS_NAVIGATION_OWNER = 'settings';
export interface SettingsPanelBinding {
  scope: WorkspaceViewScope;
  dialogId: string;
  change: (tab: SettingTab, anchor?: string) => void;
  read: () => SettingState;
  readSection: (anchor: string) => HTMLElement | null;
}
const panelKey = (scope: WorkspaceViewScope, dialogId: string) =>
  JSON.stringify([scope.workspaceId, scope.viewId, dialogId]);

/** Only mounted panel callbacks live here; offered pages stay in the command registry. */
export class SettingsNavigationOwner extends Service {
  private readonly panels = new Map<string, SettingsPanelBinding>();
  constructor(
    private readonly dialogs: WorkspaceDialogService,
    private readonly context: WorkspaceViewContextService,
    private readonly memory: SettingsMemory
  ) {
    super();
  }

  registerPanel(binding: SettingsPanelBinding): () => void {
    const key = panelKey(binding.scope, binding.dialogId);
    this.panels.set(key, binding);
    return () => {
      if (this.panels.get(key) === binding) this.panels.delete(key);
    };
  }

  private offered(scope: WorkspaceViewScope, targetId: string) {
    return this.context.navigation.resolveParent(
      scope,
      navigationActionId(scope, SETTINGS_NAVIGATION_OWNER, targetId)
    ).ok;
  }

  activate(
    scope: WorkspaceViewScope,
    targetId: string,
    args: NavigationArgs
  ): void {
    this.change(scope, targetId as SettingTab, args);
  }

  activateSection(
    scope: WorkspaceViewScope,
    tab: SettingTab,
    anchor: string,
    args: NavigationArgs
  ): void {
    if (!this.context.isActiveScope(scope))
      throw new NavigationActivationError('stale');
    if (!this.offered(scope, `${tab}/${anchor}`))
      throw new NavigationActivationError('blocked');
    this.change(scope, tab, args, anchor);
  }

  private change(
    scope: WorkspaceViewScope,
    targetId: SettingTab,
    args: NavigationArgs,
    anchor?: string
  ): void {
    if (!this.context.isActiveScope(scope))
      throw new NavigationActivationError('stale');
    if (Object.keys(args).length || !this.offered(scope, targetId))
      throw new NavigationActivationError('blocked');
    const existing = this.dialogs.dialogs$.value.findLast(
      dialog => dialog.type === 'setting'
    );
    const panel = existing
      ? this.panels.get(panelKey(scope, existing.id))
      : undefined;
    if (existing) {
      // A dialog still mounting must not be duplicated or updated through initial props.
      if (!panel || panel.dialogId !== existing.id)
        throw new NavigationActivationError('blocked');
      panel.change(targetId, anchor);
    } else {
      this.dialogs.open('setting', {
        activeTab: targetId as SettingTab,
        scrollAnchor: anchor,
      });
    }
  }

  open(scope: WorkspaceViewScope): void {
    if (!this.context.isActiveScope(scope))
      throw new NavigationActivationError('stale');
    if (this.dialogs.dialogs$.value.some(dialog => dialog.type === 'setting'))
      return;
    // The dialog reopens on the page shown last, while the viewer may open it.
    const last = this.memory.lastPage;
    this.activate(
      scope,
      last && this.offered(scope, last) ? last : 'appearance',
      {}
    );
  }

  readPanel(
    scope: WorkspaceViewScope
  ): { id: string; tab: string; anchor?: string } | null {
    if (!this.context.isActiveScope(scope)) return null;
    const existing = this.dialogs.dialogs$.value.findLast(
      dialog => dialog.type === 'setting'
    );
    const panel = existing
      ? this.panels.get(panelKey(scope, existing.id))
      : undefined;
    if (!panel) return null;
    const state = panel.read();
    if (!this.offered(scope, state.activeTab)) return null;
    return {
      id: panel.dialogId,
      tab: state.activeTab,
      anchor: state.scrollAnchor,
    };
  }

  readTarget(
    scope: WorkspaceViewScope,
    targetId: string
  ): WorkspaceNavigationTarget | null {
    if (!this.offered(scope, targetId)) return null;
    const dialog = this.readPanel(scope);
    if (!dialog || dialog.tab !== targetId) return null;
    const value = this.context.snapshot(scope);
    if (!value.ok) return null;
    const { snapshot } = value;
    return {
      ...scope,
      revision: snapshot.revision,
      surfaceId: snapshot.surfaceId,
      route: snapshot.route,
      title: snapshot.title,
      dialog,
    };
  }
  readSectionTarget(
    scope: WorkspaceViewScope,
    tab: SettingTab,
    anchor: string
  ): WorkspaceNavigationTarget | null {
    if (!this.offered(scope, `${tab}/${anchor}`)) return null;
    const target = this.readTarget(scope, tab);
    if (!target || target.dialog?.anchor !== anchor) return null;
    const panel = this.panels.get(panelKey(scope, target.dialog.id));
    const section = panel?.readSection(anchor);
    return section?.isConnected && section.dataset.settingSection === anchor
      ? target
      : null;
  }
}
